/**
 * Glossary — plain-English definitions for every metric shown across
 * the What-If simulator and other portfolio views. Used by tooltip
 * handlers so the same copy is consistent everywhere. Mirror to the
 * /glossary page so users can browse all definitions in one place.
 */
export const WHATIF_GLOSSARY = {
  'total_value': {
    term: 'Total Value',
    def: 'Your portfolio\'s projected worth at the end of the time-machine horizon. = stocks value + any accumulated cash from dividends when DRIP is off. Includes price growth/drop compounded annually.',
  },
  'stocks_value': {
    term: 'Stocks Value',
    def: 'The market value of all equity positions at the projected end-of-horizon price (starting price × price growth per year).',
  },
  'cash': {
    term: 'Cash',
    def: 'Accumulated dividends held as cash. Only non-zero when DRIP is off for some or all of the horizon — those dividends don\'t buy more shares, so they sit in cash.',
  },
  'annual_income': {
    term: 'Annual Income',
    def: 'Projected dividends paid in the final (end-of-horizon) year. Grows from today\'s dividend rate by the annual dividend-growth slider.',
  },
  'monthly_run_rate': {
    term: 'Monthly Run-Rate',
    def: 'Annual Income ÷ 12. A quick "if nothing changes, this is what my portfolio spits out per month" estimate.',
  },
  'yield': {
    term: 'Yield',
    def: 'Annual Income ÷ Total Value × 100. The effective yield-on-cost at the projected end-state. Lower here isn\'t always bad — it can mean prices grew faster than dividends.',
  },
  'avg_risk': {
    term: 'Avg Risk',
    def: 'Average Income-Reliability score across all holdings, 1 (safest) to 10 (most speculative). Colored dot: emerald (1-3) / amber (4-6) / rose (7-10).',
  },
  'positions': {
    term: 'Positions',
    def: 'Count of distinct tickers in the simulated portfolio. Multiple lots of the same ticker are treated as one position.',
  },
  'dca': {
    term: 'Dollar-Cost Averaging (DCA)',
    def: 'Buying a fixed dollar amount on a regular schedule (usually monthly), regardless of price. Reduces timing risk — you buy more shares when prices are low and fewer when they\'re high.',
  },
  'drip': {
    term: 'Dividend Reinvestment (DRIP)',
    def: 'Automatically using each dividend payment to buy more shares of the same stock. Over time this compounds — more shares → bigger dividends → even more shares.',
  },
  'time_machine': {
    term: 'Time Machine',
    def: 'The horizon (years) over which the simulator projects. Always applied to BOTH Now and What-If sides — it\'s a "what will my portfolio look like in N years" question.',
  },
  'dividend_growth': {
    term: 'Annual Dividend Growth',
    def: 'The expected yearly increase in dividend payments, compounding. Historical S&P 500 average is ~5-6%. Dividend Kings often grow 7-10%+.',
  },
  'price_upside': {
    term: 'Stock Price Upside/Downside',
    def: 'Expected annual change in share price. Positive = market grows. Negative = market drops. Applied as a compound annual rate across the whole portfolio.',
  },
  'income_projection': {
    term: 'Income Projection Chart',
    def: 'Year-by-year dividend income over the time-machine horizon. Shows both Now (grey) and What-If (violet) lines so you can see the scenario\'s long-term impact.',
  },
  // --- Sectors -------------------------------------------------------------
  'sector': {
    term: 'Sector',
    def: 'Broad industry category assigned to each stock (e.g. Financials, Technology, Healthcare). Diversifying across sectors reduces single-sector shock risk.',
  },
  'sector_Technology': {
    term: 'Technology',
    def: 'Hardware, software, semiconductors, cloud, internet services. Historically higher growth, lower yields.',
  },
  'sector_Financials': {
    term: 'Financials',
    def: 'Banks, insurance, asset managers, exchanges. Typically steady dividends; sensitive to interest rates.',
  },
  'sector_Healthcare': {
    term: 'Healthcare',
    def: 'Pharmaceuticals, biotech, medical devices, health insurers. Mix of growth (biotech) and defensive (large pharma) names.',
  },
  'sector_Energy': {
    term: 'Energy',
    def: 'Oil & gas producers, pipelines, refiners. Commodity-price-sensitive; often high yields with variable safety.',
  },
  'sector_Utilities': {
    term: 'Utilities',
    def: 'Electric, gas, and water providers. Regulated monopolies with slow growth + stable high yields. Classic "sleep well at night" sector.',
  },
  'sector_Consumer Staples': {
    term: 'Consumer Staples',
    def: 'Food, beverages, household goods, tobacco — things people buy in any economy. Defensive, lower growth, stable dividends. Many Dividend Kings live here.',
  },
  'sector_Consumer Discretionary': {
    term: 'Consumer Discretionary',
    def: 'Retailers, restaurants, travel, apparel, autos. Cyclical — booms in expansions, suffers in recessions.',
  },
  'sector_Industrials': {
    term: 'Industrials',
    def: 'Machinery, aerospace, transportation, construction. Tied to capex cycles; includes many long-time dividend payers.',
  },
  'sector_Real Estate': {
    term: 'Real Estate',
    def: 'REITs (Real Estate Investment Trusts) — required to distribute 90%+ of income. Usually high yields; sensitive to interest rates.',
  },
  'sector_Communication Services': {
    term: 'Communication Services',
    def: 'Telecom, media, internet platforms. Mix of mature dividend payers (AT&T, Verizon) and growth names (Google, Netflix).',
  },
  'sector_Materials': {
    term: 'Materials',
    def: 'Miners, chemicals, metals, paper. Commodity-cycle sensitive; yields vary widely with input prices.',
  },
  'sector_Other': {
    term: 'Other',
    def: 'Stocks without a clean sector classification (often funds, ADRs, or newly-listed companies).',
  },
  // Aliases — map Yahoo/app-side sector strings onto the canonical entries
  // above so InfoTip k="sector_<App Name>" resolves correctly regardless
  // of which naming convention the page is using.
  'sector_Financial Services': {
    term: 'Financial Services',
    def: 'Banks, insurance, asset managers, exchanges, credit services. Typically steady dividends; sensitive to interest rates.',
  },
  'sector_Consumer Defensive': {
    term: 'Consumer Defensive',
    def: 'Food, beverages, household goods, tobacco — things people buy in any economy. Defensive, lower growth, stable dividends. Many Dividend Kings live here.',
  },
  'sector_Consumer Cyclical': {
    term: 'Consumer Cyclical',
    def: 'Retailers, restaurants, travel, apparel, autos. Cyclical — booms in expansions, suffers in recessions.',
  },
  'sector_Basic Materials': {
    term: 'Basic Materials',
    def: 'Miners, chemicals, metals, paper, construction materials. Commodity-cycle sensitive; yields vary widely with input prices.',
  },
  'sector_ETF / Other': {
    term: 'ETF / Other',
    def: 'ETFs, CEFs, and funds without a sector. Includes broad-market index funds, sector-specific ETFs, covered-call funds, and bond ETFs — anything that holds many tickers rather than representing one company.',
  },
  'sector_Cash': {
    term: 'Cash',
    def: 'Accumulated dividends not yet reinvested. Grows when DRIP is off or after the DRIP-for-N-years window ends.',
  },
};

export function getDef(key) {
  return WHATIF_GLOSSARY[key] || null;
}
